import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./OrderCorePage.css";
import {
    getOrderCoreIssues,
    getOrderCoreMismatchReport,
    getOrderCoreOrder,
    getOrderCoreOrders,
    getOrderCoreOverview,
    getOrderCoreSnapshot,
} from "../api/orderCoreApi";

// Страница шага S9a: read-only просмотр агрегата Order Core (заказы, журнал
// событий заказа, открытые расхождения, отчёт рассинхрона с report_orders,
// состояние контура). Никаких действий над заказами — этап 1 только зеркалит.

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "18px",
    maxWidth: "1560px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const tabs = [
    ["orders", "Заказы агрегата"],
    ["issues", "Расхождения"],
    ["mismatch", "Сверка с отчётностью"],
    ["state", "Состояние контура"],
];

const CANONICAL_STATUSES = [
    ["received", "получен"],
    ["normalized", "нормализован"],
    ["awaiting_crm", "ожидает карточку"],
    ["in_crm", "в CRM"],
    ["confirmed_to_source", "подтверждён в Tabletki"],
    ["shipping_prepared", "готов к отправке"],
    ["in_transit", "в пути"],
    ["delivered", "доставлен"],
    ["completed", "завершён"],
    ["cancelled", "отменён"],
    ["returned", "возврат"],
    ["deleted", "удалён"],
    ["needs_attention", "требует внимания"],
];

const SOURCE_SYSTEMS = [
    ["tabletki", "Tabletki"],
    ["salesdrive_manual", "ручная карточка SalesDrive"],
    ["prom", "Prom"],
    ["rozetka", "Rozetka"],
];

const STATUS_TONE = {
    cancelled: "bad",
    returned: "bad",
    deleted: "bad",
    needs_attention: "warn",
    completed: "good",
    delivered: "good",
    confirmed_to_source: "good",
};

const isoDate = (date) => {
    const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return shifted.toISOString().slice(0, 10);
};

const today = new Date();
const defaultPeriodTo = isoDate(today);
const defaultPeriodFrom = isoDate(new Date(today.getTime() - 6 * 24 * 3600 * 1000));

const fmtDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const fmtAmount = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtInt = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString("ru-RU") : String(value);
};

const errorText = (error) => error?.response?.data?.detail || error?.message || "Неизвестная ошибка";

const shortId = (value) => {
    const text = String(value || "");
    return text.length > 14 ? `${text.slice(0, 8)}…${text.slice(-4)}` : text || "—";
};

const Kpi = ({ label, value, sub, tone = "default" }) => (
    <div className={`oc-kpi${tone !== "default" ? ` tone-${tone}` : ""}`}>
        <span className="lbl">{label}</span>
        <span className="val">{value}</span>
        {sub ? <span className="sub">{sub}</span> : null}
    </div>
);

const Panel = ({ title, hint, actions, children }) => (
    <section className="oc-card">
        <div className="oc-panel-head">
            <div>
                <h3>{title}</h3>
                {hint ? <p>{hint}</p> : null}
            </div>
            {actions ? <div className="oc-panel-acts">{actions}</div> : null}
        </div>
        <div className="oc-panel-pad">{children}</div>
    </section>
);

const Json = ({ value }) => (
    <pre className="oc-json">{value === null || value === undefined ? "—" : JSON.stringify(value, null, 2)}</pre>
);

const OrderCorePage = () => {
    const [activeTab, setActiveTab] = useState("orders");
    const [periodFrom, setPeriodFrom] = useState(defaultPeriodFrom);
    const [periodTo, setPeriodTo] = useState(defaultPeriodTo);
    const [error, setError] = useState("");

    const [overview, setOverview] = useState(null);

    const [orders, setOrders] = useState({ items: [], total: 0, status_counts: {} });
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [canonicalStatus, setCanonicalStatus] = useState("");
    const [sourceSystem, setSourceSystem] = useState("");
    const [attentionOnly, setAttentionOnly] = useState(false);
    const [issuesOnly, setIssuesOnly] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const pageSize = 50;

    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [snapshot, setSnapshot] = useState(null);

    const [issues, setIssues] = useState({ items: [], total: 0, kind_counts: {} });
    const [issuesLoading, setIssuesLoading] = useState(false);
    const [issueKind, setIssueKind] = useState("");
    const [issueResolved, setIssueResolved] = useState("open");

    const [mismatch, setMismatch] = useState(null);
    const [mismatchLoading, setMismatchLoading] = useState(false);

    const loadOverview = useCallback(async () => {
        try {
            setOverview(await getOrderCoreOverview());
        } catch (loadError) {
            setError(errorText(loadError));
        }
    }, []);

    const loadOrders = useCallback(async () => {
        setOrdersLoading(true);
        try {
            const data = await getOrderCoreOrders({
                period_from: periodFrom,
                period_to: periodTo,
                canonical_status: canonicalStatus,
                source_system: sourceSystem,
                attention: attentionOnly ? true : undefined,
                has_open_issues: issuesOnly ? true : undefined,
                search,
                limit: pageSize,
                offset: page * pageSize,
            });
            setOrders(data);
            setError("");
        } catch (loadError) {
            setError(errorText(loadError));
        } finally {
            setOrdersLoading(false);
        }
    }, [periodFrom, periodTo, canonicalStatus, sourceSystem, attentionOnly, issuesOnly, search, page]);

    const loadIssues = useCallback(async () => {
        setIssuesLoading(true);
        try {
            const data = await getOrderCoreIssues({
                kind: issueKind,
                resolved: issueResolved === "all" ? undefined : issueResolved === "resolved",
                limit: 200,
            });
            setIssues(data);
            setError("");
        } catch (loadError) {
            setError(errorText(loadError));
        } finally {
            setIssuesLoading(false);
        }
    }, [issueKind, issueResolved]);

    const loadMismatch = useCallback(async () => {
        setMismatchLoading(true);
        try {
            const data = await getOrderCoreMismatchReport({ period_from: periodFrom, period_to: periodTo, samples: 50 });
            setMismatch(data);
            setError("");
        } catch (loadError) {
            setError(errorText(loadError));
        } finally {
            setMismatchLoading(false);
        }
    }, [periodFrom, periodTo]);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { if (activeTab === "orders") loadOrders(); }, [activeTab, loadOrders]);
    useEffect(() => { if (activeTab === "issues") loadIssues(); }, [activeTab, loadIssues]);
    useEffect(() => { if (activeTab === "mismatch" && !mismatch) loadMismatch(); }, [activeTab, mismatch, loadMismatch]);

    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setDetailLoading(true);
        setSnapshot(null);
        getOrderCoreOrder(selectedId)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((loadError) => { if (!cancelled) setError(errorText(loadError)); })
            .finally(() => { if (!cancelled) setDetailLoading(false); });
        return () => { cancelled = true; };
    }, [selectedId]);

    const openOrder = (orderId) => {
        setSelectedId((current) => (current === orderId ? null : orderId));
    };

    const showSnapshot = async (orderId, snapshotId) => {
        try {
            setSnapshot(await getOrderCoreSnapshot(orderId, snapshotId));
        } catch (loadError) {
            setError(errorText(loadError));
        }
    };

    const applySearch = (event) => {
        event.preventDefault();
        setPage(0);
        setSearch(searchInput.trim());
    };

    const totalPages = Math.max(1, Math.ceil((orders.total || 0) / pageSize));
    const statusCounts = useMemo(
        () => Object.entries(orders.status_counts || {}).sort((left, right) => right[1] - left[1]),
        [orders.status_counts],
    );
    const openIssueKinds = useMemo(
        () => Object.entries(overview?.open_issues || {}).sort((left, right) => right[1] - left[1]),
        [overview],
    );
    const salesdriveCursor = (overview?.cursors || []).find((cursor) => cursor.cursor_kind === "salesdrive_update_at");

    return (
        <div className="order-core-page" style={pageStyle}>
            <div className="oc-headrow">
                <div className="oc-htitle">
                    <h1>Контур заказов</h1>
                    <span className="oc-crumb">агрегат Order Core — только просмотр</span>
                </div>
                <div className="oc-hacts">
                    <input type="date" className="oc-inp sm" value={periodFrom} onChange={(event) => { setPeriodFrom(event.target.value); setPage(0); setMismatch(null); }} />
                    <input type="date" className="oc-inp sm" value={periodTo} onChange={(event) => { setPeriodTo(event.target.value); setPage(0); setMismatch(null); }} />
                    <button type="button" className="oc-btn oc-btn-ghost oc-btn-sm" onClick={() => { loadOverview(); if (activeTab === "orders") loadOrders(); if (activeTab === "issues") loadIssues(); if (activeTab === "mismatch") loadMismatch(); }}>
                        Обновить
                    </button>
                </div>
            </div>

            {error ? <div className="oc-note err"><span className="dot" />{error}</div> : (
                <div className="oc-note"><span className="dot" />Данные обновлены {fmtDateTime(overview?.generated_at)}</div>
            )}

            <div className="oc-kpi-grid">
                <Kpi label="Заказов в агрегате" value={fmtInt(overview?.counts?.orders)} sub={`за сутки +${fmtInt(overview?.counts?.orders_last_day)}`} tone="blue" />
                <Kpi label="Событий" value={fmtInt(overview?.counts?.events)} sub={`за сутки +${fmtInt(overview?.counts?.events_last_day)}`} />
                <Kpi label="Снимков" value={fmtInt(overview?.counts?.snapshots)} sub={`за сутки +${fmtInt(overview?.counts?.snapshots_last_day)}`} />
                <Kpi
                    label="Открытых расхождений"
                    value={fmtInt(overview?.open_issues_total)}
                    sub={openIssueKinds.length ? openIssueKinds.map(([kind, count]) => `${kind}: ${count}`).join(", ") : "нет"}
                    tone={overview?.open_issues_total ? "warn" : "good"}
                />
                <Kpi label="Требуют внимания" value={fmtInt(overview?.counts?.attention_orders)} sub="attention_flag" tone={overview?.counts?.attention_orders ? "warn" : "good"} />
                <Kpi
                    label="Бюджет poller-а"
                    value={`${fmtInt(salesdriveCursor?.requests_today)} / ${fmtInt(salesdriveCursor?.daily_budget)}`}
                    sub={`курсор ${salesdriveCursor?.cursor_value || "—"}`}
                />
                <Kpi
                    label="Теневая запись"
                    value={overview?.shadow_config?.enabled ? overview?.shadow_config?.mode : "выключена"}
                    sub={(overview?.shadow_config?.points || []).join(", ") || "точки не заданы"}
                    tone={overview?.shadow_config?.enabled ? "good" : "warn"}
                />
            </div>

            <div className="oc-subtabs">
                {tabs.map(([key, label]) => (
                    <button key={key} type="button" className={`oc-subtab${activeTab === key ? " active" : ""}`} onClick={() => setActiveTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            {activeTab === "orders" && (
                <div className="oc-view">
                    <Panel
                        title="Заказы агрегата"
                        hint="Период считается по дате заказа источника; клик по строке открывает журнал заказа"
                        actions={(
                            <form className="oc-filters" onSubmit={applySearch}>
                                <select className="oc-inp sm" value={canonicalStatus} onChange={(event) => { setCanonicalStatus(event.target.value); setPage(0); }}>
                                    <option value="">все статусы</option>
                                    {CANONICAL_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                </select>
                                <select className="oc-inp sm" value={sourceSystem} onChange={(event) => { setSourceSystem(event.target.value); setPage(0); }}>
                                    <option value="">все источники</option>
                                    {SOURCE_SYSTEMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                </select>
                                <label className="oc-check"><input type="checkbox" checked={attentionOnly} onChange={(event) => { setAttentionOnly(event.target.checked); setPage(0); }} />внимание</label>
                                <label className="oc-check"><input type="checkbox" checked={issuesOnly} onChange={(event) => { setIssuesOnly(event.target.checked); setPage(0); }} />с расхождениями</label>
                                <input className="oc-inp sm" placeholder="номер, id, ТТН, телефон, клиент" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
                                <button type="submit" className="oc-btn oc-btn-primary oc-btn-sm">Найти</button>
                            </form>
                        )}
                    >
                        <div className="oc-chips">
                            {statusCounts.map(([status, count]) => (
                                <span key={status} className={`oc-chip${STATUS_TONE[status] ? ` tone-${STATUS_TONE[status]}` : ""}`}>
                                    {(CANONICAL_STATUSES.find(([value]) => value === status) || [status, status])[1]}: <b>{fmtInt(count)}</b>
                                </span>
                            ))}
                        </div>
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead>
                                    <tr>
                                        <th>Заказ</th>
                                        <th>Дата</th>
                                        <th>Магазин</th>
                                        <th>Канонический статус</th>
                                        <th>Tabletki</th>
                                        <th>SalesDrive</th>
                                        <th>Поставщик</th>
                                        <th className="num">Сумма</th>
                                        <th className="num">Себестоимость</th>
                                        <th>ТТН</th>
                                        <th className="num">Расхождения</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ordersLoading && !orders.items.length ? (
                                        <tr className="oc-empty-row"><td colSpan={11}>Загрузка…</td></tr>
                                    ) : null}
                                    {!ordersLoading && !orders.items.length ? (
                                        <tr className="oc-empty-row"><td colSpan={11}>За период заказов нет</td></tr>
                                    ) : null}
                                    {orders.items.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={`oc-row${selectedId === item.id ? " selected" : ""}${item.attention_flag ? " attention" : ""}`}
                                            onClick={() => openOrder(item.id)}
                                        >
                                            <td>
                                                <div className="oc-nm">{item.source_order_number || shortId(item.source_order_id)}</div>
                                                <div className="oc-sub-meta">{item.source_system} · {shortId(item.source_order_id)}</div>
                                            </td>
                                            <td>
                                                {fmtDateTime(item.source_created_at || item.first_seen_at)}
                                                {item.source_created_at ? null : <div className="oc-sub-meta">по первому появлению</div>}
                                            </td>
                                            <td>{item.store_name || <span className="oc-mut">не определён</span>}</td>
                                            <td>
                                                <span className={`oc-badge${STATUS_TONE[item.canonical_status] ? ` tone-${STATUS_TONE[item.canonical_status]}` : ""}`}>
                                                    {item.canonical_status_title}
                                                </span>
                                                {item.attention_reason ? <div className="oc-sub-meta">{item.attention_reason}</div> : null}
                                            </td>
                                            <td>{item.tabletki_status_effective || "—"}</td>
                                            <td>
                                                {item.salesdrive_status_id ?? "—"}
                                                {item.salesdrive_order_id ? <div className="oc-sub-meta">карточка {item.salesdrive_order_id}</div> : <div className="oc-sub-meta">нет карточки</div>}
                                            </td>
                                            <td>{item.supplier_code || "—"}{item.supplier_mode === "split" ? <div className="oc-sub-meta">разбит</div> : null}</td>
                                            <td className="num">{fmtAmount(item.order_amount)}</td>
                                            <td className="num">{fmtAmount(item.cost_amount)}</td>
                                            <td>{item.tracking_number || "—"}</td>
                                            <td className="num">{item.open_issues ? <span className="oc-badge tone-warn">{item.open_issues}</span> : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="oc-pager">
                            <span className="oc-mut">Всего {fmtInt(orders.total)} · страница {page + 1} из {totalPages}</span>
                            <div className="oc-pager-acts">
                                <button type="button" className="oc-btn oc-btn-ghost oc-btn-sm" disabled={page === 0} onClick={() => setPage((current) => Math.max(0, current - 1))}>Назад</button>
                                <button type="button" className="oc-btn oc-btn-ghost oc-btn-sm" disabled={page + 1 >= totalPages} onClick={() => setPage((current) => current + 1)}>Вперёд</button>
                            </div>
                        </div>
                    </Panel>

                    {selectedId ? (
                        <OrderDetail
                            detail={detail}
                            loading={detailLoading}
                            snapshot={snapshot}
                            onShowSnapshot={showSnapshot}
                            onClose={() => setSelectedId(null)}
                        />
                    ) : null}
                </div>
            )}

            {activeTab === "issues" && (
                <div className="oc-view">
                    <Panel
                        title="Расхождения (issues)"
                        hint="Открывает и закрывает их reconciliation воркера; здесь только просмотр"
                        actions={(
                            <div className="oc-filters">
                                <select className="oc-inp sm" value={issueResolved} onChange={(event) => setIssueResolved(event.target.value)}>
                                    <option value="open">открытые</option>
                                    <option value="resolved">закрытые</option>
                                    <option value="all">все</option>
                                </select>
                                <select className="oc-inp sm" value={issueKind} onChange={(event) => setIssueKind(event.target.value)}>
                                    <option value="">все виды</option>
                                    {Object.keys(issues.kind_counts || {}).map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                                </select>
                            </div>
                        )}
                    >
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead>
                                    <tr>
                                        <th>Вид</th>
                                        <th>Заказ</th>
                                        <th>Обнаружено</th>
                                        <th>Закрыто</th>
                                        <th>Детали</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {issuesLoading && !issues.items.length ? <tr className="oc-empty-row"><td colSpan={5}>Загрузка…</td></tr> : null}
                                    {!issuesLoading && !issues.items.length ? <tr className="oc-empty-row"><td colSpan={5}>Расхождений нет</td></tr> : null}
                                    {issues.items.map((issue) => (
                                        <tr key={issue.id}>
                                            <td>
                                                <div className="oc-nm">{issue.kind_title}</div>
                                                <div className="oc-sub-meta">{issue.kind}</div>
                                            </td>
                                            <td>
                                                {issue.order_id ? (
                                                    <button type="button" className="oc-link" onClick={() => { setActiveTab("orders"); setSelectedId(issue.order_id); }}>
                                                        {issue.source_order_number || shortId(issue.source_order_id)}
                                                    </button>
                                                ) : <span className="oc-mut">без заказа</span>}
                                                {issue.canonical_status ? <div className="oc-sub-meta">{issue.canonical_status}</div> : null}
                                            </td>
                                            <td>{fmtDateTime(issue.detected_at)}</td>
                                            <td>{issue.resolved_at ? `${fmtDateTime(issue.resolved_at)} (${issue.resolution})` : <span className="oc-mut">открыто</span>}</td>
                                            <td><Json value={issue.details} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
            )}

            {activeTab === "mismatch" && (
                <div className="oc-view">
                    <Panel
                        title="Сверка агрегата с отчётностью"
                        hint="report_orders (legacy) против агрегата за выбранный период — основа критериев контрольного периода"
                        actions={<button type="button" className="oc-btn oc-btn-primary oc-btn-sm" onClick={loadMismatch} disabled={mismatchLoading}>{mismatchLoading ? "Считаю…" : "Пересчитать"}</button>}
                    >
                        {mismatch ? (
                            <>
                                <div className="oc-kpi-grid">
                                    <Kpi label="Заказов в агрегате" value={fmtInt(mismatch.totals.core_orders)} sub={`сумма ${fmtAmount(mismatch.totals.core_order_amount)}`} tone="blue" />
                                    <Kpi
                                        label="Из них без суммы"
                                        value={fmtInt(mismatch.totals.core_orders_without_amount)}
                                        sub="в итог агрегата не входят"
                                        tone={mismatch.totals.core_orders_without_amount ? "warn" : "good"}
                                    />
                                    <Kpi label="Строк SalesDrive" value={fmtInt(mismatch.totals.report_salesdrive_rows)} sub={`сумма ${fmtAmount(mismatch.totals.report_salesdrive_amount)}`} />
                                    <Kpi label="Строк Tabletki" value={fmtInt(mismatch.totals.report_tabletki_rows)} sub={`сумма ${fmtAmount(mismatch.totals.report_tabletki_amount)}`} />
                                    <Kpi label="Расхождений всего" value={fmtInt(mismatch.mismatch_total)} tone={mismatch.mismatch_total ? "warn" : "good"} />
                                    <Kpi label="Δ сумм (агрегат − SalesDrive)" value={fmtAmount(mismatch.totals.amount_delta_core_vs_salesdrive)} tone={Number(mismatch.totals.amount_delta_core_vs_salesdrive) ? "warn" : "good"} />
                                    <Kpi label="Δ сумм (агрегат − Tabletki)" value={fmtAmount(mismatch.totals.amount_delta_core_vs_tabletki)} tone={Number(mismatch.totals.amount_delta_core_vs_tabletki) ? "warn" : "good"} />
                                </div>
                                <ul className="oc-notes">
                                    {mismatch.notes.map((note) => <li key={note}>{note}</li>)}
                                </ul>
                                {mismatch.dimensions.map((dimension) => (
                                    <div key={dimension.key} className="oc-dim">
                                        <div className="oc-dim-head">
                                            <span className="oc-nm">{dimension.title}</span>
                                            <span className={`oc-badge${dimension.count ? " tone-warn" : " tone-good"}`}>{fmtInt(dimension.count)}</span>
                                            {dimension.truncated ? <span className="oc-mut">показаны первые {dimension.samples.length}</span> : null}
                                        </div>
                                        {dimension.count ? (
                                            <div className="oc-tbl-wrap">
                                                <table className="oc-tbl">
                                                    <thead>
                                                        <tr>
                                                            <th>Заказ</th>
                                                            <th>В агрегате</th>
                                                            <th>В report_orders</th>
                                                            <th>Прочее</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dimension.samples.map((sample, index) => (
                                                            <tr key={`${dimension.key}-${sample.order_id || sample.report_order_id || index}`}>
                                                                <td>
                                                                    {sample.order_id ? (
                                                                        <button type="button" className="oc-link" onClick={() => { setActiveTab("orders"); setSelectedId(sample.order_id); }}>
                                                                            {sample.order_number || shortId(sample.source_order_id)}
                                                                        </button>
                                                                    ) : shortId(sample.source_order_id)}
                                                                </td>
                                                                <td>{sample.core === undefined ? "—" : (typeof sample.core === "object" ? JSON.stringify(sample.core) : String(sample.core ?? "—"))}</td>
                                                                <td>{sample.report === undefined ? "—" : String(sample.report ?? "—")}</td>
                                                                <td className="oc-sub-meta">
                                                                    {[
                                                                        sample.canonical_status ? `статус ${sample.canonical_status}` : null,
                                                                        sample.order_date ? `дата ${fmtDateTime(sample.order_date)}` : null,
                                                                        sample.order_created_at ? `дата отчётности ${fmtDateTime(sample.order_created_at)}` : null,
                                                                        sample.report_source ? `строка ${sample.report_source}` : null,
                                                                        sample.status_id !== undefined && sample.status_id !== null ? `status_id ${sample.status_id}` : null,
                                                                        sample.order_amount !== undefined && sample.order_amount !== null ? `сумма ${fmtAmount(sample.order_amount)}` : null,
                                                                        sample.is_deleted ? "помечена удалённой" : null,
                                                                    ].filter(Boolean).join(" · ") || "—"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </>
                        ) : <div className="oc-mut">{mismatchLoading ? "Загрузка…" : "Нажмите «Пересчитать»"}</div>}
                    </Panel>
                </div>
            )}

            {activeTab === "state" && (
                <div className="oc-view">
                    <Panel title="Курсоры и бюджет" hint="poller SalesDrive и служебные курсоры воркера">
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead>
                                    <tr>
                                        <th>Источник</th>
                                        <th>Курсор</th>
                                        <th>Значение</th>
                                        <th>Последний запуск</th>
                                        <th>Последний успех</th>
                                        <th className="num">Запросов сегодня</th>
                                        <th className="num">Бюджет</th>
                                        <th>Ошибка</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(overview?.cursors || []).length ? null : <tr className="oc-empty-row"><td colSpan={8}>Курсоров нет</td></tr>}
                                    {(overview?.cursors || []).map((cursor) => (
                                        <tr key={`${cursor.source}-${cursor.scope}-${cursor.cursor_kind}`}>
                                            <td>{cursor.source}<div className="oc-sub-meta">{cursor.scope}</div></td>
                                            <td>{cursor.cursor_kind}</td>
                                            <td>{cursor.cursor_value || "—"}</td>
                                            <td>{fmtDateTime(cursor.last_run_at)}</td>
                                            <td>{fmtDateTime(cursor.last_success_at)}</td>
                                            <td className="num">{fmtInt(cursor.requests_today)}</td>
                                            <td className="num">{fmtInt(cursor.daily_budget)}</td>
                                            <td className={cursor.last_error ? "oc-err-cell" : "oc-mut"}>{cursor.last_error || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <div className="oc-split2">
                        <Panel title="Обращения к SalesDrive за сутки" hint="журнал sync_runs воркера">
                            <table className="oc-tbl kv">
                                <tbody>
                                    <tr><td>Запросов</td><td>{fmtInt(overview?.sync_runs_last_day?.runs)}</td></tr>
                                    <tr><td>Получено карточек</td><td>{fmtInt(overview?.sync_runs_last_day?.items)}</td></tr>
                                    <tr className={overview?.sync_runs_last_day?.errors ? "hl" : ""}><td>Ошибок</td><td>{fmtInt(overview?.sync_runs_last_day?.errors)}</td></tr>
                                    <tr><td>Последний запрос</td><td>{fmtDateTime(overview?.sync_runs_last_day?.last_started_at)}</td></tr>
                                </tbody>
                            </table>
                        </Panel>

                        <Panel title="Заказы по каноническому статусу" hint="весь агрегат, без фильтра по периоду">
                            <table className="oc-tbl kv">
                                <tbody>
                                    {Object.entries(overview?.canonical_counts || {}).sort((left, right) => right[1] - left[1]).map(([status, count]) => (
                                        <tr key={status}>
                                            <td>{(CANONICAL_STATUSES.find(([value]) => value === status) || [status, status])[1]}</td>
                                            <td>{fmtInt(count)}</td>
                                        </tr>
                                    ))}
                                    {Object.keys(overview?.canonical_counts || {}).length ? null : <tr className="oc-empty-row"><td colSpan={2}>Пусто</td></tr>}
                                </tbody>
                            </table>
                        </Panel>
                    </div>

                    <Panel title="Суточные метрики" hint="снимок сигналов «legacy не сломан» за последние 7 дней (order_core_daily_metrics)">
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead><tr><th>Дата</th><th>Метрика</th><th className="num">Значение</th></tr></thead>
                                <tbody>
                                    {(overview?.daily_metrics || []).length ? null : <tr className="oc-empty-row"><td colSpan={3}>Метрики ещё не собирались</td></tr>}
                                    {(overview?.daily_metrics || []).map((metric) => (
                                        <tr key={`${metric.metric_date}-${metric.metric}`}>
                                            <td>{metric.metric_date}</td>
                                            <td>{metric.metric}</td>
                                            <td className="num">{fmtAmount(metric.value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </div>
            )}
        </div>
    );
};

const OrderDetail = ({ detail, loading, snapshot, onShowSnapshot, onClose }) => {
    if (loading && !detail) {
        return <Panel title="Заказ">Загрузка…</Panel>;
    }
    if (!detail) {
        return null;
    }
    const order = detail.order;
    const field = (label, value) => (
        <tr key={label}><td>{label}</td><td>{value === null || value === undefined || value === "" ? "—" : value}</td></tr>
    );

    return (
        <section className="oc-card oc-detail">
            <div className="oc-panel-head">
                <div>
                    <h3>Заказ {order.source_order_number || shortId(order.source_order_id)}</h3>
                    <p>{order.source_system} · {order.source_order_id} · агрегат #{order.id}</p>
                </div>
                <div className="oc-panel-acts">
                    <button type="button" className="oc-btn oc-btn-ghost oc-btn-sm" onClick={onClose}>Закрыть</button>
                </div>
            </div>

            <div className="oc-panel-pad oc-detail-grid">
                <div>
                    <h4>Проекция</h4>
                    <table className="oc-tbl kv">
                        <tbody>
                            {field("Канонический статус", order.canonical_status_title)}
                            {field("Статус Tabletki", order.tabletki_status_effective)}
                            {field("Статус SalesDrive", order.salesdrive_status_id)}
                            {field("Дата заказа", fmtDateTime(order.source_created_at))}
                            {field("Первое появление", fmtDateTime(order.first_seen_at))}
                            {field("Последнее событие источника", fmtDateTime(order.last_source_event_at))}
                            {field("Дедлайн подтверждения", fmtDateTime(order.tabletki_confirm_deadline_at))}
                            {field("Магазин", order.store_name ? `${order.store_name} (${order.store_code || order.business_store_id})` : null)}
                            {field("Предприятие", order.enterprise_code)}
                            {field("Определение магазина", order.store_resolution)}
                            {field("Поставщик", order.supplier_code ? `${order.supplier_code} (${order.supplier_mode || "single"})` : null)}
                            {field("Сумма заказа", fmtAmount(order.order_amount))}
                            {field("Себестоимость", `${fmtAmount(order.cost_amount)}${order.cost_source ? ` · ${order.cost_source}` : ""}`)}
                            {field("Оплата", order.payment_method_external)}
                            {field("ТТН", order.tracking_number ? `${order.tracking_number} (${order.tracking_provider || "—"})` : null)}
                            {field("Клиент", order.customer_name)}
                            {field("Телефон", order.customer_phone)}
                            {field("Причина отказа", order.cancel_reason_external)}
                            {field("Внимание", order.attention_flag ? (order.attention_reason || "да") : "нет")}
                            {field("Версия проекции", `${order.projection_version} (событие #${order.last_event_id || "—"})`)}
                        </tbody>
                    </table>
                </div>

                <div>
                    <h4>Позиции ({detail.lines.length})</h4>
                    <div className="oc-tbl-wrap">
                        <table className="oc-tbl">
                            <thead><tr><th>#</th><th>Товар</th><th className="num">Кол-во</th><th className="num">Цена</th><th className="num">Себест.</th><th>Поставщик</th></tr></thead>
                            <tbody>
                                {detail.lines.length ? null : <tr className="oc-empty-row"><td colSpan={6}>Позиций нет</td></tr>}
                                {detail.lines.map((line) => (
                                    <tr key={line.id} className={line.is_current ? "" : "sub"}>
                                        <td>{line.line_no}</td>
                                        <td>
                                            <div className="oc-nm">{line.product_name || line.internal_product_code || line.external_product_code}</div>
                                            <div className="oc-sub-meta">{line.internal_product_code || line.external_product_code} · {line.mapping_status || "—"}{line.is_current ? "" : " · заменена"}</div>
                                        </td>
                                        <td className="num">{fmtAmount(line.qty_ordered)}{line.qty_confirmed_to_source ? <div className="oc-sub-meta">подтв. {fmtAmount(line.qty_confirmed_to_source)}</div> : null}</td>
                                        <td className="num">{fmtAmount(line.unit_price)}</td>
                                        <td className="num">{fmtAmount(line.cost_price)}</td>
                                        <td>{line.supplier_code || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h4>Карточки SalesDrive ({detail.cards.length})</h4>
                    <div className="oc-tbl-wrap">
                        <table className="oc-tbl">
                            <thead><tr><th>Карточка</th><th>Роль</th><th>Статус</th><th>Версия</th><th>ТТН</th><th>Привязка</th></tr></thead>
                            <tbody>
                                {detail.cards.length ? null : <tr className="oc-empty-row"><td colSpan={6}>Карточек нет</td></tr>}
                                {detail.cards.map((card) => (
                                    <tr key={card.id}>
                                        <td>{card.salesdrive_order_id}<div className="oc-sub-meta">{card.external_id || "без externalId"}</div></td>
                                        <td>{card.role}</td>
                                        <td>{card.status_id ?? "—"}</td>
                                        <td>{card.version ?? "—"}<div className="oc-sub-meta">{fmtDateTime(card.updated_at_external)}</div></td>
                                        <td>{card.tracking_number || "—"}</td>
                                        <td>{card.linked_by}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h4>Строки отчётности ({detail.report_orders.length})</h4>
                    <div className="oc-tbl-wrap">
                        <table className="oc-tbl">
                            <thead><tr><th>Источник</th><th>Карточка</th><th>Статус</th><th>Дата</th><th className="num">Сумма</th><th className="num">Себест.</th><th>ТТН</th></tr></thead>
                            <tbody>
                                {detail.report_orders.length ? null : <tr className="oc-empty-row"><td colSpan={7}>Строк в report_orders нет</td></tr>}
                                {detail.report_orders.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.source}</td>
                                        <td>{row.salesdrive_order_id || "—"}</td>
                                        <td>{row.status_id ?? "—"}<div className="oc-sub-meta">{row.status_name || ""}</div></td>
                                        <td>{fmtDateTime(row.order_created_at)}</td>
                                        <td className="num">{fmtAmount(row.order_amount)}</td>
                                        <td className="num">{fmtAmount(row.supplier_cost_total)}</td>
                                        <td>{row.tracking_number || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div className="oc-panel-pad">
                <h4>Журнал событий ({detail.events.length})</h4>
                <div className="oc-tbl-wrap">
                    <table className="oc-tbl">
                        <thead><tr><th>Когда</th><th>Что</th><th>Источник</th><th>Кто</th><th>Данные</th></tr></thead>
                        <tbody>
                            {detail.events.length ? null : <tr className="oc-empty-row"><td colSpan={5}>Событий нет</td></tr>}
                            {detail.events.map((event) => (
                                <tr key={event.id}>
                                    <td>{fmtDateTime(event.occurred_at)}<div className="oc-sub-meta">записано {fmtDateTime(event.recorded_at)}</div></td>
                                    <td><div className="oc-nm">{event.event_title}</div><div className="oc-sub-meta">{event.event_type}</div></td>
                                    <td>{event.source}</td>
                                    <td>{event.actor || "—"}</td>
                                    <td><Json value={event.payload} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="oc-split2">
                    <div>
                        <h4>Снимки источников ({detail.snapshots.length})</h4>
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead><tr><th>Когда</th><th>Источник</th><th>Версия</th><th>Хэш</th><th /></tr></thead>
                                <tbody>
                                    {detail.snapshots.length ? null : <tr className="oc-empty-row"><td colSpan={5}>Снимков нет</td></tr>}
                                    {detail.snapshots.map((item) => (
                                        <tr key={item.id}>
                                            <td>{fmtDateTime(item.seen_at)}</td>
                                            <td>{item.source}</td>
                                            <td>{item.source_version ?? "—"}{item.version_authoritative ? "" : <div className="oc-sub-meta">версия не авторитетна</div>}</td>
                                            <td className="oc-mono">{String(item.payload_hash).slice(0, 12)}…</td>
                                            <td><button type="button" className="oc-btn oc-btn-ghost oc-btn-sm" onClick={() => onShowSnapshot(order.id, item.id)}>Показать</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {snapshot ? (
                            <>
                                <h4>Снимок #{snapshot.id} · {snapshot.source} · {fmtDateTime(snapshot.seen_at)}</h4>
                                <Json value={snapshot.payload} />
                            </>
                        ) : null}
                    </div>

                    <div>
                        <h4>Внешние ссылки ({detail.external_refs.length})</h4>
                        <table className="oc-tbl">
                            <thead><tr><th>Система</th><th>Тип</th><th>Значение</th></tr></thead>
                            <tbody>
                                {detail.external_refs.length ? null : <tr className="oc-empty-row"><td colSpan={3}>Ссылок нет</td></tr>}
                                {detail.external_refs.map((ref) => (
                                    <tr key={ref.id}><td>{ref.system}</td><td>{ref.ref_type}</td><td className="oc-mono">{ref.ref_value}</td></tr>
                                ))}
                            </tbody>
                        </table>

                        <h4>Исходящие действия ({detail.outbox.length})</h4>
                        <div className="oc-tbl-wrap">
                            <table className="oc-tbl">
                                <thead><tr><th>Куда</th><th>Действие</th><th>Статус</th><th className="num">Попыток</th><th>Результат</th></tr></thead>
                                <tbody>
                                    {detail.outbox.length ? null : <tr className="oc-empty-row"><td colSpan={5}>Записей нет</td></tr>}
                                    {detail.outbox.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.target}</td>
                                            <td>{item.action}<div className="oc-sub-meta">{item.mode}</div></td>
                                            <td>{item.status}{item.error ? <div className="oc-err-cell">{item.error}</div> : null}</td>
                                            <td className="num">{item.attempts}</td>
                                            <td>{item.result_ref || "—"}{item.response_status ? <div className="oc-sub-meta">HTTP {item.response_status}</div> : null}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <h4>Расхождения заказа ({detail.issues.length})</h4>
                        <table className="oc-tbl">
                            <thead><tr><th>Вид</th><th>Обнаружено</th><th>Состояние</th></tr></thead>
                            <tbody>
                                {detail.issues.length ? null : <tr className="oc-empty-row"><td colSpan={3}>Расхождений нет</td></tr>}
                                {detail.issues.map((issue) => (
                                    <tr key={issue.id}>
                                        <td>{issue.kind_title}<div className="oc-sub-meta">{issue.kind}</div></td>
                                        <td>{fmtDateTime(issue.detected_at)}</td>
                                        <td>{issue.resolved_at ? `закрыто ${fmtDateTime(issue.resolved_at)}` : "открыто"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OrderCorePage;
